import { BaseDashlet } from "../../lq_js_common/dashboard/base_dashlet";
import { lerpGreenToRedViaOrange, scaleNumber, formatMbpsCapacity } from "../../helpers/scaling";
import { disposeTooltips, enableTooltips, formatTimeRange, unixToISO } from "../../helpers/tooltips";
import { EXPLAIN_GOOD_STATUS_PREFIX } from "../../helpers/explain_issues";
import { ensureTitleBar } from "../../helpers/show_limits";
import { applyExecutiveMessage, buildExecutiveData, createExecutiveState } from "./executive_data";

const QOO_TOOLTIP = "QoO (Quality of Outcome) estimates web user experience from latency and TCP retransmits. Scores run 0-100, where higher is better.";
const EXEC_SENTIMENT_UP_THRESHOLD = 53;
const EXEC_SENTIMENT_DOWN_THRESHOLD = 44;
const EXEC_PERIOD_SUMMARY_TOOLTIP = "Period Summary";
const EXEC_HEATMAP_FLOW_TEXT = "Upload on top, Download on bottom.";

function fmt(value, digits = 0, suffix = "") {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    const rounded = digits > 0 ? n.toFixed(digits) : Math.round(n).toString();
    return `${rounded}${suffix}`;
}

function escapeHtmlAttr(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function qooTooltipIcon() {
    return `
        <button
            type="button"
            class="btn btn-link btn-sm exec-inline-info exec-qoo-tooltip"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-html="true"
            data-bs-trigger="hover focus click"
            title="${escapeHtmlAttr(QOO_TOOLTIP)}"
            aria-label="About QoO"
        >
            <i class="fas fa-circle-info"></i>
        </button>
    `.trim();
}

function clampNumber(value, min, max) {
    return Math.max(min, Math.min(max, Number(value) || 0));
}

function qualityFromRange(value, max, invert = false) {
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    const normalized = (clampNumber(value, 0, max) / max) * 100;
    return invert ? (100 - normalized) : normalized;
}

function qualityFromQoo(score) {
    return qualityFromRange(score, 100, false);
}

function qualityFromRtt(ms) {
    return qualityFromRange(ms, 200, true);
}

function qualityFromRetransmit(percent) {
    return qualityFromRange(percent, 10, true);
}

function qualityFromCapacity(percent) {
    return qualityFromRange(percent, 100, true);
}

function sentimentState(quality) {
    if (quality === null) {
        return {
            icon: "fa-minus",
            tone: "text-muted",
            label: "No data",
        };
    }
    if (quality >= EXEC_SENTIMENT_UP_THRESHOLD) {
        return {
            icon: "fa-thumbs-up",
            tone: "text-success",
            label: "Healthy",
        };
    }
    if (quality <= EXEC_SENTIMENT_DOWN_THRESHOLD) {
        return {
            icon: "fa-thumbs-down",
            tone: "text-danger",
            label: "Needs attention",
        };
    }
    return {
        icon: "fa-minus",
        tone: "text-warning",
        label: "Watch",
    };
}

function sentimentIndicator(quality) {
    const state = sentimentState(quality);
    return `<i class='fas ${state.icon} exec-sentiment-icon ${state.tone}' aria-hidden='true' title='${state.label}'></i>`;
}

function colorByQoo(score) {
    const clamped = Math.max(0, Math.min(100, Number(score) || 0));
    return lerpGreenToRedViaOrange(clamped, 100);
}

function colorByRtt(ms) {
    const clamped = Math.max(0, Math.min(200, Number(ms) || 0));
    return lerpGreenToRedViaOrange(200 - clamped, 200);
}

function colorByRetransmit(percent) {
    const clamped = Math.max(0, Math.min(10, Number(percent) || 0));
    return lerpGreenToRedViaOrange(10 - clamped, 10);
}

function colorByCapacity(percent) {
    const clamped = Math.max(0, Math.min(100, Number(percent) || 0));
    return lerpGreenToRedViaOrange(100 - clamped, 100);
}

const EXEC_HEATMAP_BUCKETS = 15;

function toFiniteNumber(value) {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function medianIgnoringNull(values) {
    const data = [];
    for (let i = 0; i < values.length; i += 1) {
        const n = toFiniteNumber(values[i]);
        if (n !== null) {
            data.push(n);
        }
    }
    if (data.length === 0) return null;
    data.sort((a, b) => a - b);
    const mid = Math.floor(data.length / 2);
    if (data.length % 2 === 1) {
        return data[mid];
    }
    return (data[mid - 1] + data[mid]) / 2;
}

function bucketizeHeatmap(topValues, bottomValues, ticks) {
    const top = Array.isArray(topValues) ? topValues : [];
    const bottom = Array.isArray(bottomValues) ? bottomValues : [];
    const tickSeries = Array.isArray(ticks) ? ticks : [];

    const len = tickSeries.length > 0
        ? tickSeries.length
        : Math.max(top.length, bottom.length);

    const alignSeries = (series) => {
        const slice = series.slice(0, len);
        if (slice.length < len) {
            return slice.concat(new Array(len - slice.length).fill(null));
        }
        return slice;
    };

    if (len <= 0) {
        return {
            top: new Array(EXEC_HEATMAP_BUCKETS).fill(null),
            bottom: new Array(EXEC_HEATMAP_BUCKETS).fill(null),
            ranges: new Array(EXEC_HEATMAP_BUCKETS).fill(null),
        };
    }

    const topTrimmed = alignSeries(top);
    const bottomTrimmed = alignSeries(bottom);
    const ticksTrimmed = alignSeries(tickSeries);

    if (len < EXEC_HEATMAP_BUCKETS) {
        const pad = EXEC_HEATMAP_BUCKETS - len;
        const ranges = new Array(pad).fill(null).concat(
            ticksTrimmed.map((tick) => {
                const t = toFiniteNumber(tick);
                return t === null ? null : { start: t, end: t };
            }),
        );
        return {
            top: new Array(pad).fill(null).concat(topTrimmed),
            bottom: new Array(pad).fill(null).concat(bottomTrimmed),
            ranges,
        };
    }

    const bounds = [];
    for (let i = 0; i < EXEC_HEATMAP_BUCKETS; i += 1) {
        const start = Math.floor((i * len) / EXEC_HEATMAP_BUCKETS);
        const nextStart = Math.floor(((i + 1) * len) / EXEC_HEATMAP_BUCKETS);
        const end = Math.max(start, nextStart - 1);
        bounds.push({ start, end });
    }

    const ranges = bounds.map(({ start, end }) => {
        if (ticksTrimmed.length === 0) return null;
        const startTick = toFiniteNumber(ticksTrimmed[start]);
        const endTick = toFiniteNumber(ticksTrimmed[end]);
        if (startTick === null || endTick === null) return null;
        return { start: startTick, end: endTick };
    });

    const topBuckets = bounds.map(({ start, end }) => medianIgnoringNull(topTrimmed.slice(start, end + 1)));
    const bottomBuckets = bounds.map(({ start, end }) => medianIgnoringNull(bottomTrimmed.slice(start, end + 1)));

    return {
        top: topBuckets,
        bottom: bottomBuckets,
        ranges,
    };
}

function formatFullSeriesRange(ticks, fallbackRanges = []) {
    const finiteTicks = (Array.isArray(ticks) ? ticks : []).map(toFiniteNumber).filter((tick) => tick !== null);
    if (finiteTicks.length >= 2) {
        return formatTimeRange(finiteTicks[0], finiteTicks[finiteTicks.length - 1]);
    }
    const finiteRanges = (Array.isArray(fallbackRanges) ? fallbackRanges : []).filter((range) => range?.start !== undefined && range?.end !== undefined);
    if (finiteRanges.length > 0) {
        return formatTimeRange(finiteRanges[0].start, finiteRanges[finiteRanges.length - 1].end);
    }
    return "selected period";
}

function summaryHealthText(metricLabel) {
    if (!metricLabel) {
        return "Thumbs are calculated from each row's own metric, so higher quality scores are healthier while lower RTT, retransmits, and utilization are healthier.";
    }
    if (metricLabel === "QoO") {
        return "Higher quality scores are healthier.";
    }
    if (metricLabel === "Median RTT") {
        return "Lower RTT is healthier.";
    }
    if (metricLabel === "TCP Retransmits") {
        return "Lower retransmit rates are healthier.";
    }
    return "Lower utilization is healthier because it leaves more headroom.";
}

function periodSummaryTooltip(metricLabel) {
    return `
        <div class="text-start">
            <div class="fw-semibold mb-1">${EXEC_PERIOD_SUMMARY_TOOLTIP}</div>
            <div>Shows the typical value for the selected time range.</div>
            <div class="mt-1">Exact statistic: median of the visible ${EXEC_HEATMAP_BUCKETS} heatmap buckets.</div>
            <div class="mt-1">Upload and download are summarized separately.</div>
            <div class="mt-1">${summaryHealthText(metricLabel)} Thumbs show Healthy, Watch, or Needs attention for that same period median.</div>
        </div>
    `.trim();
}

function summaryValueTooltip(metricLabel, directionLabel, valueText, statusLabel, rangeText) {
    return `
        <div class="text-start">
            <div class="fw-semibold mb-1">${metricLabel} ${directionLabel}</div>
            <div><strong>Typical value:</strong> Period median of the visible heatmap buckets.</div>
            <div><strong>Range:</strong> ${rangeText}</div>
            <div><strong>Status:</strong> ${statusLabel}</div>
            <div><strong>Value:</strong> ${valueText}</div>
        </div>
    `.trim();
}

function summaryValueAriaLabel(metricLabel, directionLabel, valueText, statusLabel, rangeText) {
    return `${metricLabel} ${directionLabel}. Typical value for ${rangeText}: ${valueText}. Status: ${statusLabel}.`;
}

function buildPeriodSummary(bucketed, ticks) {
    return {
        top: medianIgnoringNull(bucketed.top),
        bottom: medianIgnoringNull(bucketed.bottom),
        rangeText: formatFullSeriesRange(ticks, bucketed.ranges),
    };
}

function renderPeriodSummary(bucketed, ticks, formatter, qualityFn, metricLabel) {
    const summary = buildPeriodSummary(bucketed, ticks);
    return summaryLine(summary.top, summary.bottom, formatter, qualityFn, metricLabel, summary.rangeText);
}

// One compact line per row: "UL / DL" plus a single sentiment cue. The full
// per-direction detail stays in the tooltip.
function summaryLine(topValue, bottomValue, formatter, qualityFn, metricLabel, rangeText) {
    const topQuality = qualityFn(topValue);
    const bottomQuality = qualityFn(bottomValue);
    const topState = sentimentState(topQuality);
    const bottomState = sentimentState(bottomQuality);
    const topText = formatter(topValue);
    const bottomText = formatter(bottomValue);

    let cueQuality = topQuality;
    if (topQuality === null || topQuality === undefined) {
        cueQuality = bottomQuality;
    } else if (bottomQuality !== null && bottomQuality !== undefined) {
        cueQuality = Math.min(topQuality, bottomQuality);
    }

    const tooltip = escapeHtmlAttr(
        `${summaryValueTooltip(metricLabel, "Upload", topText, topState.label, rangeText)}`
        + `${summaryValueTooltip(metricLabel, "Download", bottomText, bottomState.label, rangeText)}`
    );
    const ariaLabel = escapeHtmlAttr(
        `${summaryValueAriaLabel(metricLabel, "Upload", topText, topState.label, rangeText)} `
        + `${summaryValueAriaLabel(metricLabel, "Download", bottomText, bottomState.label, rangeText)}`
    );

    return `
        <button
            type="button"
            class="exec-latest exec-summary-line exec-summary-button"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-html="true"
            data-bs-trigger="hover focus click"
            title="${tooltip}"
            aria-label="${ariaLabel}"
        >
            <span class="exec-summary-number">${compactPair(topText, bottomText)}</span>
            ${sentimentIndicator(cueQuality)}
        </button>
    `;
}

// "70 ms" / "30.1%" -> share the unit once: "70/70 ms", "13/30.1%". Keeps the
// fixed-width summary column from overflowing left over the heatmap cells.
function splitMeasure(text) {
    const match = String(text).match(/^([-\d.,–—]+)(\s*)(.*)$/);
    if (!match) return { number: String(text), unit: "" };
    return { number: match[1], unit: `${match[2]}${match[3]}` };
}

function compactPair(topText, bottomText) {
    const top = splitMeasure(topText);
    const bottom = splitMeasure(bottomText);
    const unit = top.unit || bottom.unit;
    return `${top.number} / ${bottom.number}${unit}`;
}

function summaryHeader(metricLabel) {
    const tooltip = escapeHtmlAttr(periodSummaryTooltip(metricLabel));
    return `
        <div class="exec-heat-summary-header">
            <button
                type="button"
                class="btn btn-link btn-sm exec-inline-info"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-html="true"
                data-bs-trigger="hover focus click"
                title="${tooltip}"
                aria-label="About ${EXEC_PERIOD_SUMMARY_TOOLTIP.toLowerCase()}"
            >
                <span class="exec-heat-summary-label">UL / DL</span>
                <i class="fas fa-info-circle" aria-hidden="true"></i>
            </button>
        </div>
    `;
}

function legendChipsHtml() {
    return `
        <span class="exec-legend-chip"><span class="exec-legend-swatch is-healthy" aria-hidden="true"></span>Healthy</span>
        <span class="exec-legend-chip"><span class="exec-legend-swatch is-watch" aria-hidden="true"></span>Watch</span>
        <span class="exec-legend-chip"><span class="exec-legend-swatch is-attention" aria-hidden="true"></span>Needs attention</span>
        <span class="exec-legend-chip"><span class="exec-legend-swatch is-empty" aria-hidden="true"></span>No data</span>
    `;
}

// Exactly one legend per page: relocate it into the Global card title row
// instead of a standalone body row. Idempotent across re-renders/rebuilds.
function ensureTitleLegend(dashlet) {
    const titleEl = document.getElementById(`${dashlet.id}_title`);
    if (!titleEl) return;
    const titleBar = ensureTitleBar(titleEl);
    if (!titleBar || titleBar.querySelector(".exec-title-legend")) return;
    const legend = document.createElement("span");
    legend.className = "exec-title-legend";
    legend.title = EXEC_HEATMAP_FLOW_TEXT;
    legend.setAttribute("aria-label", `Heatmap legend. ${EXEC_HEATMAP_FLOW_TEXT}`);
    legend.innerHTML = legendChipsHtml();
    titleBar.classList.add("exec-title-has-legend");
    titleBar.appendChild(legend);
}

function heatmapCellsFromBucketed(bucketed, colorFn) {
    const topWindow = bucketed.top;
    const bottomWindow = bucketed.bottom;
    const total = EXEC_HEATMAP_BUCKETS;
    let html = "";
    for (let i = 0; i < total; i += 1) {
        const top = topWindow[i];
        const bottom = bottomWindow[i];
        const topOk = top !== null && top !== undefined;
        const bottomOk = bottom !== null && bottom !== undefined;
        if (!topOk && !bottomOk) {
            html += "<div class='exec-heat-cell split empty' aria-hidden='true'></div>";
            continue;
        }
        html += `
            <div class="exec-heat-cell split" aria-hidden="true">
                <div class="exec-split-grid">
                    <div class="exec-split${topOk ? "" : " empty"}" style="${topOk ? `background:${colorFn(top)}` : ""}"></div>
                    <div class="exec-split${bottomOk ? "" : " empty"}" style="${bottomOk ? `background:${colorFn(bottom)}` : ""}"></div>
                </div>
            </div>
        `;
    }
    return html;
}

function severityRatio(value, maxValue, higherIsWorse) {
    if (!Number.isFinite(value) || !Number.isFinite(maxValue) || maxValue <= 0) return null;
    let ratio = value / maxValue;
    if (!higherIsWorse) {
        ratio = 1 - ratio;
    }
    if (!Number.isFinite(ratio)) return null;
    return Math.max(0, Math.min(1, ratio));
}

function severityFromRatio(ratio) {
    if (ratio === null || ratio === undefined) return null;
    if (ratio >= 0.8) return "red";
    if (ratio >= 0.5) return "orange";
    return null;
}

function downgradeSeverity(severity, steps) {
    if (!severity || !steps) return severity;
    const ladder = ["red", "orange", "info"];
    const idx = ladder.indexOf(severity);
    if (idx === -1) return severity;
    const next = Math.min(idx + steps, ladder.length - 1);
    return ladder[next];
}

function severityRank(severity) {
    if (severity === "red") return 2;
    if (severity === "orange") return 1;
    if (severity === "info") return 0;
    return -1;
}

function periodMetricScore(topValues, bottomValues, ticks, lowerIsWorse = false) {
    const summary = buildPeriodSummary(bucketizeHeatmap(topValues, bottomValues, ticks), ticks);
    const values = [summary.top, summary.bottom].filter((value) => value !== null);
    if (values.length === 0) {
        return lowerIsWorse ? Number.POSITIVE_INFINITY : -1;
    }
    return lowerIsWorse ? Math.min(...values) : Math.max(...values);
}

function metricKeyFromLabel(label) {
    const value = String(label || "").toLowerCase();
    if (value.includes("qoo") || value.includes("quality")) return "qoo";
    if (value.includes("rtt") || value.includes("latency")) return "rtt";
    if (value.includes("retransmit") || value.includes("retx")) return "retransmit";
    if (value.includes("utilization") || value.includes("utilisation")) return "utilization";
    if (value.includes("capacity")) return "capacity";
    return null;
}

function buildGlobalHeatmapIssues({
    metricLabel,
    topValues,
    bottomValues,
    ticks,
    maxValue,
    higherIsWorse,
    formatValue,
    topN = 3,
    severityDowngrade = 0,
}) {
    const bucketed = bucketizeHeatmap(topValues, bottomValues, ticks);
    const issues = [];
    let eligibleBuckets = 0;
    let goodBuckets = 0;
    for (let i = 0; i < bucketed.top.length; i += 1) {
        const top = bucketed.top[i];
        const bottom = bucketed.bottom[i];
        const topRatio = severityRatio(top, maxValue, higherIsWorse);
        const bottomRatio = severityRatio(bottom, maxValue, higherIsWorse);
        const hasData = topRatio !== null || bottomRatio !== null;
        if (!hasData) continue;
        eligibleBuckets += 1;
        const ratio = Math.max(topRatio ?? -1, bottomRatio ?? -1);
        const severityRaw = severityFromRatio(ratio);
        if (!severityRaw) {
            goodBuckets += 1;
        }
        const severity = downgradeSeverity(severityRaw, severityDowngrade);
        if (!severity) continue;
        const range = bucketed.ranges[i];
        const timeRange = range ? formatTimeRange(range.start, range.end) : null;
        const topText = Number.isFinite(top) ? formatValue(top) : "—";
        const bottomText = Number.isFinite(bottom) ? formatValue(bottom) : "—";
        issues.push({
            time_range: timeRange,
            severity,
            issue: `${metricLabel} ${higherIsWorse ? "high" : "low"}`,
            details: `UL ${topText}, DL ${bottomText}`,
            // Additive structured hints for deterministic insight grouping.
            metric: metricKeyFromLabel(metricLabel),
            kind: higherIsWorse ? "high" : "low",
            direction: "both",
            node: null,
            window: range && Number.isFinite(range.start) && Number.isFinite(range.end)
                ? { start: unixToISO(range.start), end: unixToISO(range.end) }
                : null,
            score: ratio,
        });
    }

    issues.sort((a, b) => {
        const rankA = severityRank(a.severity);
        const rankB = severityRank(b.severity);
        if (rankA !== rankB) return rankB - rankA;
        if (b.score !== a.score) return b.score - a.score;
        return 0;
    });

    const selectedIssues = issues.slice(0, topN).map((issue) => ({
        time_range: issue.time_range,
        severity: issue.severity,
        issue: issue.issue,
        details: issue.details,
        metric: issue.metric,
        kind: issue.kind,
        direction: issue.direction,
        node: issue.node,
        window: issue.window,
    }));
    if (eligibleBuckets <= 0) return selectedIssues;

    const pctGood = Math.max(0, Math.min(100, Math.round((goodBuckets / eligibleBuckets) * 100)));
    return [
        {
            time_range: null,
            severity: "info",
            issue: `${EXPLAIN_GOOD_STATUS_PREFIX}${pctGood}% of the time`,
            details: null,
            metric: metricKeyFromLabel(metricLabel),
            kind: "status",
            direction: "both",
            node: null,
            window: null,
        },
        ...selectedIssues,
    ];
}

function nodeTypeCode(type) {
    const value = String(type || "").trim();
    if (!value) return "";
    const lower = value.toLowerCase();
    if (lower === "circuit") return "C";
    if (lower === "site") return "S";
    if (lower === "ap") return "AP";
    return value.replace(/[^a-z0-9]/gi, "").slice(0, 2).toUpperCase() || "?";
}

function nodeTypeChip(type) {
    const code = nodeTypeCode(type);
    if (!code) return "";
    const safe = escapeHtmlAttr(type);
    return `<span class="exec-node-chip" title="Node type: ${safe}" aria-label="Node type: ${safe}">${escapeHtmlAttr(code)}</span>`;
}

function heatRow(label, badge, link, topValues, bottomValues, ticks, colorFn, formatter, qualityFn = () => null, displayLabel = null, metricLabel = null) {
    const bucketed = bucketizeHeatmap(topValues, bottomValues, ticks);
    const rowLabel = displayLabel || label;
    const labelHtml = link
        ? `<a href="${link}" class="text-decoration-none">${rowLabel}</a>`
        : rowLabel;
    const summaryMetricLabel = metricLabel || label;
    return `
        <div class="exec-heat-row">
            <div class="exec-heat-label text-truncate" title="${escapeHtmlAttr(label)}">
                ${nodeTypeChip(badge)}
                <span class="exec-heat-name fw-semibold text-truncate">${labelHtml}</span>
            </div>
            <div class="exec-heat-cells" aria-hidden="true">${heatmapCellsFromBucketed(bucketed, colorFn)}</div>
            ${renderPeriodSummary(bucketed, ticks, formatter, qualityFn, summaryMetricLabel)}
        </div>
    `;
}

function rowScore(row, metric) {
    const values = row?.metrics || {};
    const ticks = row?.ticks || [];
    if (metric === "qoo") {
        return periodMetricScore(values.qoo_up || [], values.qoo_down || [], ticks, true);
    }
    if (metric === "rtt") {
        return periodMetricScore(values.rtt_up || [], values.rtt_down || [], ticks);
    }
    if (metric === "retransmit") {
        return periodMetricScore(values.retransmit_up || [], values.retransmit_down || [], ticks);
    }
    return periodMetricScore(values.utilization_up || [], values.utilization_down || [], ticks);
}

function helperQuery(context) {
    const params = new URLSearchParams();
    params.set("context", context);
    params.set("nodeId", String(window.nodeId || ""));
    if (context === "site") {
        params.set("site", String(window.siteId || ""));
    }
    return params.toString();
}

class ExecutiveBaseDashlet extends BaseDashlet {
    constructor(slot, context) {
        super(slot);
        this.context = context;
        this.state = createExecutiveState(context);
        this.rendered = false;
        this.size = 6;
    }

    canBeSlowedDown() {
        return true;
    }

    channelRequests() {
        if (this.context === "shaper") {
            return ["ShaperDetail", "ShaperExecutive"];
        }
        return ["SiteExecutive"];
    }

    setup() {
        super.setup();
        this.render();
    }

    applyRenderState() {
        const card = document.getElementById(this.id);
        if (!card) return;
        if (!this.rendered) {
            card.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            card.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                card.classList.remove("animate__animated", "animate__pulse");
            }, 700);
        }
        this.rendered = true;
    }

    buildContainer() {
        const base = super.buildContainer();
        this.contentId = `${this.id}_content`;
        const content = document.createElement("div");
        content.id = this.contentId;
        content.innerHTML = "<div class='text-muted small'><i class='fa fa-spinner fa-spin'></i> Loading executive data...</div>";
        base.appendChild(content);
        return base;
    }

    onMessage(msg) {
        if (!msg?.Data) return;
        const changed = applyExecutiveMessage(this.context, this.state, msg);
        if (changed) {
            this.render();
        }
    }

    executiveData() {
        return buildExecutiveData(this.context, this.state);
    }
}

function overviewChip(icon, label, value, titleText) {
    const title = escapeHtmlAttr(titleText || `${label} ${value}`.trim());
    // Keep a real space between label and value so the accessible name reads
    // "CPU 14.0%" rather than "CPU14.0%".
    const labelHtml = label ? `<span class="exec-overview-label">${escapeHtmlAttr(label)}</span> ` : "";
    const accessibleLabel = `${label ? `${label} ` : ""}${value}`.trim();
    return `<span class="exec-overview-chip" aria-label="${escapeHtmlAttr(accessibleLabel)}" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-html="true" title="${title}"><i class="fas ${icon}" aria-hidden="true"></i>${labelHtml}<span class="exec-overview-value">${escapeHtmlAttr(value)}</span></span>`;
}

function overviewDivider() {
    return '<span class="exec-overview-divider" aria-hidden="true"></span>';
}

export class ExecutiveSnapshotDashlet extends ExecutiveBaseDashlet {
    title() {
        return "Overview";
    }

    channelRequests() {
        if (this.context === "shaper") {
            return ["ShaperDetail", "ShaperExecutive"];
        }
        return ["SiteDetail", "SiteExecutive"];
    }

    overviewChips(h) {
        const chips = [];
        if (this.context === "shaper") {
            if (h.last_seen) {
                const seen = new Date(Number(h.last_seen) * 1000);
                const version = (typeof h.version === "string" && h.version.trim()) ? h.version.trim() : "Before 2.1";
                chips.push(overviewChip("fa-clock", "", seen.toLocaleTimeString(), `Last seen ${seen.toLocaleString()} · LibreQoS ${version}`));
            }
            if (Number.isFinite(Number(h.cpu_percent))) {
                const cpu = Number(h.cpu_percent).toFixed(1);
                chips.push(overviewChip("fa-microchip", "CPU", `${cpu}%`, `Average CPU ${cpu}%`));
            }
            if (Number.isFinite(Number(h.memory_percent)) && h.memory_percent !== null) {
                const mem = (Number(h.memory_percent) * 100).toFixed(1);
                chips.push(overviewChip("fa-memory", "Mem", `${mem}%`, `Memory ${mem}%`));
            }
            const down = Number(h.capacity_down_mbps || 0);
            const up = Number(h.capacity_up_mbps || 0);
            if (down || up) {
                chips.push(overviewChip("fa-gauge", "", `↓${formatMbpsCapacity(down)} ↑${formatMbpsCapacity(up)}`, `Capacity ↓${formatMbpsCapacity(down)} / ↑${formatMbpsCapacity(up)}`));
            }
            if (h.active_circuit_count !== null && h.active_circuit_count !== undefined) {
                chips.push(overviewChip("fa-plug", "", `${h.active_circuit_count} circuits`, `Active circuits: ${h.active_circuit_count}`));
            }
        } else {
            if (h.site_type) {
                chips.push(overviewChip("fa-sitemap", "", String(h.site_type), `Site type: ${h.site_type}`));
            }
            const dl = Number(h.download_bandwidth_mbps || 0);
            const ul = Number(h.upload_bandwidth_mbps || 0);
            if (dl || ul) {
                chips.push(overviewChip("fa-gauge", "Limits", `${formatMbpsCapacity(dl)} / ${formatMbpsCapacity(ul)}`, `Configured limits ${formatMbpsCapacity(dl)} / ${formatMbpsCapacity(ul)}`));
            }
        }
        return chips;
    }

    setOverviewTitle(name) {
        if (!name) return;
        const titleEl = document.getElementById(this.id + "_title");
        if (!titleEl) return;
        titleEl.innerHTML = `<span class="exec-overview-title"><i class="fa fa-server" aria-hidden="true"></i> ${escapeHtmlAttr(name)}</span>`;
    }

    render() {
        const target = document.getElementById(this.contentId);
        if (!target) return;
        const data = this.executiveData();
        if (!data) {
            target.innerHTML = "<div class='text-muted small'>Waiting for executive summary data...</div>";
            return;
        }
        this.applyRenderState();
        const h = data.header || {};
        this.setOverviewTitle(h.title);

        const contextChips = this.overviewChips(h);
        const metricChips = [
            overviewChip("fa-bullseye", "QoO", `${fmt(h.qoo_up)} / ${fmt(h.qoo_down)}`, QOO_TOOLTIP),
            overviewChip("fa-stopwatch", "RTT", fmt(h.rtt, 0, " ms"), `Median RTT (upload / download): ${fmt(h.rtt, 0, " ms")}`),
            overviewChip("fa-rotate-left", "Retx", `${fmt(h.retransmit_up, 1, "%")} / ${fmt(h.retransmit_down, 1, "%")}`, "TCP retransmits (upload / download)"),
            overviewChip("fa-chart-line", "Util", `${fmt(h.utilization_up, 0, "%")} / ${fmt(h.utilization_down, 0, "%")}`, "Utilization (upload / download)"),
        ];
        const chips = contextChips.length ? [...contextChips, overviewDivider(), ...metricChips] : metricChips;

        disposeTooltips(target);
        target.innerHTML = `<div class="exec-overview">${chips.join("")}</div>`;
        enableTooltips(target);
    }
}

export class ExecutiveGlobalHeatmapDashlet extends ExecutiveBaseDashlet {
    constructor(slot, context) {
        super(slot, context);
        this.explainGroups = null;
    }

    title() {
        return "Global Heatmap";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainGroups !== null;
    }

    explain() {
        if (!this.explainGroups || this.explainGroups.length === 0) return null;
        return this.explainGroups;
    }

    render() {
        const target = document.getElementById(this.contentId);
        if (!target) return;
        const data = this.executiveData();
        if (!data) {
            target.innerHTML = "<div class='text-muted small'>Waiting for heatmap data...</div>";
            this.explainGroups = null;
            return;
        }
        this.applyRenderState();
        const g = data.global;
        const ticks = g.ticks || [];
        const rows = [
            heatRow("Quality Score", null, null, g.qoo_up || [], g.qoo_down || [], ticks, colorByQoo, (v) => fmt(v), qualityFromQoo, `Quality Score ${qooTooltipIcon()}`, "QoO"),
            heatRow("RTT", null, null, g.rtt_up || [], g.rtt_down || [], ticks, colorByRtt, (v) => fmt(v, 0, " ms"), qualityFromRtt, null, "Median RTT"),
            heatRow("TCP Retransmits", null, null, g.retransmit_up || [], g.retransmit_down || [], ticks, colorByRetransmit, (v) => fmt(v, 1, "%"), qualityFromRetransmit, null, "TCP Retransmits"),
            heatRow("Utilization", null, null, g.utilization_up || [], g.utilization_down || [], ticks, colorByCapacity, (v) => fmt(v, 0, "%"), qualityFromCapacity, null, "Utilization"),
        ].join("");
        disposeTooltips(target);
        target.innerHTML = `
            <div class="card shadow-sm border-0">
                <div class="card-body py-1">
                    <div class="exec-heat-head">
                        <div></div>
                        <div></div>
                        ${summaryHeader(null)}
                    </div>
                    <div class="exec-heat-rows">${rows}</div>
                </div>
            </div>
        `;
        ensureTitleLegend(this);
        enableTooltips(target);

        this.explainGroups = [
            {
                region: "Global Heatmap — QoO",
                issues: buildGlobalHeatmapIssues({
                    metricLabel: "QoO",
                    topValues: g.qoo_up || [],
                    bottomValues: g.qoo_down || [],
                    ticks,
                    maxValue: 100,
                    higherIsWorse: false,
                    formatValue: (v) => fmt(v),
                }),
            },
            {
                region: "Global Heatmap — RTT",
                issues: buildGlobalHeatmapIssues({
                    metricLabel: "RTT",
                    topValues: g.rtt_up || [],
                    bottomValues: g.rtt_down || [],
                    ticks,
                    maxValue: 200,
                    higherIsWorse: true,
                    formatValue: (v) => fmt(v, 0, " ms"),
                }),
            },
            {
                region: "Global Heatmap — TCP Retransmits",
                issues: buildGlobalHeatmapIssues({
                    metricLabel: "TCP Retransmits",
                    topValues: g.retransmit_up || [],
                    bottomValues: g.retransmit_down || [],
                    ticks,
                    maxValue: 10,
                    higherIsWorse: true,
                    formatValue: (v) => fmt(v, 1, "%"),
                    severityDowngrade: 1,
                }),
            },
            {
                region: "Global Heatmap — Utilization",
                issues: buildGlobalHeatmapIssues({
                    metricLabel: "Utilization",
                    topValues: g.utilization_up || [],
                    bottomValues: g.utilization_down || [],
                    ticks,
                    maxValue: 100,
                    higherIsWorse: true,
                    formatValue: (v) => fmt(v, 0, "%"),
                }),
            },
        ].filter((group) => Array.isArray(group.issues) && group.issues.length > 0);

        if (this.explainGroups.length === 0) {
            this.explainGroups = [];
        }
    }
}

class ExecutiveMetricHeatmapDashlet extends ExecutiveBaseDashlet {
    constructor(slot, context, metric, title, icon) {
        super(slot, context);
        this.metric = metric;
        this.myTitle = title;
        this.myIcon = icon;
    }

    title() {
        return this.myTitle;
    }

    metricConfig() {
        if (this.metric === "rtt") {
            return {
                color: colorByRtt,
                format: (v) => fmt(v, 0, " ms"),
                latestQuality: qualityFromRtt,
                selectTop: (row) => row.metrics.rtt_up || [],
                selectBottom: (row) => row.metrics.rtt_down || [],
            };
        }
        if (this.metric === "retransmit") {
            return {
                color: colorByRetransmit,
                format: (v) => fmt(v, 1, "%"),
                latestQuality: qualityFromRetransmit,
                selectTop: (row) => row.metrics.retransmit_up || [],
                selectBottom: (row) => row.metrics.retransmit_down || [],
            };
        }
        if (this.metric === "qoo") {
            return {
                color: colorByQoo,
                format: (v) => fmt(v),
                latestQuality: qualityFromQoo,
                selectTop: (row) => row.metrics.qoo_up || [],
                selectBottom: (row) => row.metrics.qoo_down || [],
            };
        }
        return {
            color: colorByCapacity,
            format: (v) => fmt(v, 0, "%"),
            latestQuality: qualityFromCapacity,
            selectTop: (row) => row.metrics.utilization_up || [],
            selectBottom: (row) => row.metrics.utilization_down || [],
        };
    }

    sortedRows(rows) {
        // Executive summaries rank infrastructure nodes (sites/APs), not
        // subscriber circuits.
        const infra = rows.filter((row) => row.row_type !== "Circuit");
        const ascending = this.metric === "qoo";
        // Score each row once, then sort (the old comparator re-bucketed every
        // row on each comparison).
        return infra
            .map((row) => ({ row, score: rowScore(row, this.metric) }))
            .sort((a, b) => (ascending ? a.score - b.score : b.score - a.score))
            .slice(0, 10)
            .map((entry) => entry.row);
    }

    render() {
        const target = document.getElementById(this.contentId);
        if (!target) return;
        const data = this.executiveData();
        if (!data) {
            target.innerHTML = "<div class='text-muted small'>Waiting for executive rows...</div>";
            return;
        }
        this.applyRenderState();
        const cfg = this.metricConfig();
        const summaryMetricLabel = this.metric === "qoo" ? "QoO" : this.myTitle;
        const sourceRows = data.rows || [];
        const rows = this.sortedRows(sourceRows).map((row) => {
            const top = cfg.selectTop(row);
            const bottom = cfg.selectBottom(row);
            return heatRow(row.label, row.badge, row.link, top, bottom, row.ticks || [], cfg.color, cfg.format, cfg.latestQuality, null, summaryMetricLabel);
        }).join("");
        const emptyMessage = sourceRows.length > 0 ? "No sites/APs to rank." : "No data yet.";
        disposeTooltips(target);
        target.innerHTML = `
                <div class="card shadow-sm border-0 h-100">
                    <div class="card-body py-1">
                        <div class="text-muted small mb-1"><i class="fas ${this.myIcon} me-1 text-primary"></i>${this.metric === "qoo" ? "Worst 10 sites/APs by quality score over selected period " + qooTooltipIcon() : "Worst 10 sites/APs over selected period"}</div>
                        <div class="exec-heat-head">
                            <div></div>
                            <div></div>
                        ${summaryHeader(summaryMetricLabel)}
                    </div>
                    <div class="exec-heat-rows">${rows || `<div class='text-muted small'>${emptyMessage}</div>`}</div>
                </div>
            </div>
        `;
        enableTooltips(target);
    }
}

export class ExecutiveRttHeatmapDashlet extends ExecutiveMetricHeatmapDashlet {
    constructor(slot, context) {
        super(slot, context, "rtt", "Median RTT", "fa-stopwatch");
    }
}

export class ExecutiveRetransmitsHeatmapDashlet extends ExecutiveMetricHeatmapDashlet {
    constructor(slot, context) {
        super(slot, context, "retransmit", "TCP Retransmits", "fa-rotate-left");
    }
}

export class ExecutiveUtilizationHeatmapDashlet extends ExecutiveMetricHeatmapDashlet {
    constructor(slot, context) {
        super(slot, context, "utilization", "Utilization", "fa-chart-line");
    }
}

export class ExecutiveQooHeatmapDashlet extends ExecutiveMetricHeatmapDashlet {
    constructor(slot, context) {
        super(slot, context, "qoo", "Quality Score (QoO)", "fa-bullseye");
    }
}

export class ExecutiveHelpersDashlet extends ExecutiveBaseDashlet {
    title() {
        return "Helper Views";
    }

    channelRequests() {
        return [];
    }

    render() {
        const target = document.getElementById(this.contentId);
        if (!target) return;
        const query = helperQuery(this.context);
        const links = [
            { href: `executive_worst_sites.html?${query}`, icon: "fa-temperature-high", label: "Top 10 Worst Performing Sites" },
            { href: `executive_oversubscribed_sites.html?${query}`, icon: "fa-chart-line", label: "Top 10 Most Over-Subscribed Sites" },
            { href: `executive_top_asns.html?${query}`, icon: "fa-globe-americas", label: "Top 10 ASNs by Traffic Volume" },
        ];
        if (this.context === "shaper") {
            links.splice(2, 0,
                { href: `executive_sites_due_upgrade.html?${query}`, icon: "fa-arrow-up-right-dots", label: "Sites Due for Upgrade" },
                { href: `executive_circuits_due_upgrade.html?${query}`, icon: "fa-wave-square", label: "Circuits Due for Upgrade" },
            );
        }
        target.innerHTML = `
            <div class="card shadow-sm border-0">
                <div class="card-body py-2">
                    <div class="d-flex flex-wrap gap-2">
                        ${links.map((l) => `<a class="btn btn-outline-primary exec-helper-button" href="${l.href}"><i class="fas ${l.icon} me-2"></i>${l.label}</a>`).join("")}
                    </div>
                </div>
            </div>
        `;
    }
}

export function formatExecutiveBits(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    return `${scaleNumber(Number(value), 1)}bps`;
}
